/**
 * Running inside the Birbly app (apps/mobile), or in a browser.
 *
 * The app is this same bundle in a Capacitor web view. Everything native it
 * needs is in ONE small script the app build adds in front of this bundle
 * (apps/mobile/src/native.ts), which publishes `window.BirblyNative` before
 * any of this runs. This file is the only place the web app looks for it, so
 * the web bundle itself carries no Capacitor code, no plugin and nothing that
 * could make a request of its own -- tools/audit-network.py keeps meaning
 * what it says for both.
 *
 * What changes in the app, and only in the app:
 *   - sign-in and sign-out open in the operating system's own browser sheet
 *     (SFSafariViewController / Custom Tabs), never in this web view -- Google
 *     refuses embedded sign-in, and a parent should see the real address bar;
 *   - Cognito sends them back to `app.birbly://auth/...`, which the app turns
 *     back into this bundle's `/auth/callback`;
 *   - links a parent shares (join, child codes, the recovery sheet) still
 *     point at the public website, not at the app's private origin.
 */

export interface NativeBridge {
  platform: 'ios' | 'android';
  /**
   * Which of the two apps this is (2026-10-19): Birbly for parents
   * (apps/mobile) or Birbly Kids (apps/mobile-kids). The same web bundle
   * runs in both; this is what keeps each to its own side. Absent in a shell
   * built before the split, which was the parents' app.
   */
  side?: AppSide;
  /** Where Cognito sends a parent after signing in, in the app. */
  callbackUri: string;
  /** Where Cognito sends a parent after signing out, in the app. */
  signedOutUri: string;
  /** Open a sign-in or sign-out page in the system browser sheet. */
  openAuth(url: string): Promise<void>;
  /** Hide the status bar for the full-screen valley, and bring it back. */
  setImmersive?(on: boolean): void;
  /** Keep the screen awake while the valley is up on a stand. */
  keepAwake?(on: boolean): void;
}

export type AppSide = 'parents' | 'kids';

declare global {
  interface Window {
    BirblyNative?: NativeBridge;
  }
}

export function native(): NativeBridge | null {
  return (typeof window !== 'undefined' && window.BirblyNative) || null;
}

export function nativeShell(): boolean {
  return native() !== null;
}

/** The public website, for links that leave this device. */
export const PUBLIC_ORIGIN = 'https://birbly.app';

/**
 * The origin to put in a link somebody else will open.
 *
 * In a browser that is wherever this page is served from. In the app it is
 * the website: the app's own origin (`capacitor://birbly.app` on iOS) is a
 * private address no other device can open.
 */
export function publicOrigin(): string {
  return nativeShell() ? PUBLIC_ORIGIN : window.location.origin;
}

/**
 * Which app this is: 'parents', 'kids', or null in a browser, where the
 * address decides and both sides are open.
 */
export function appSide(): AppSide | null {
  const shell = native();
  return shell ? (shell.side ?? 'parents') : null;
}

const HOME_KEY = 'birbly.app.home';

/**
 * Which side of Birbly the app opens on: '/' (parents) or '/me' (a child's
 * own page). A family tablet set up for a child should reopen as the child's
 * page, not as a parent sign-in screen. Browsers have addresses for this and
 * ignore it.
 */
export function rememberAppHome(path: '/' | '/me'): void {
  // Two apps now: each opens on its own side and nothing needs remembering.
  if (!nativeShell() || appSide() !== null) return;
  try {
    localStorage.setItem(HOME_KEY, path);
  } catch {
    // Storage blocked: the app opens on the parents' side, which is safe.
  }
}

export function appHome(): '/' | '/me' {
  if (appSide() === 'kids') return '/me';
  if (appSide() === 'parents') return '/';
  try {
    return localStorage.getItem(HOME_KEY) === '/me' ? '/me' : '/';
  } catch {
    return '/';
  }
}
